import React, { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { cn } from "../../utils/cn";

/**
 * DESIGN.md modal — thin styled wrapper around @headlessui/react Dialog.
 *  - white canvas panel
 *  - 12px radius
 *  - hairline border, light shadow only (color-block first)
 *  - 32px internal padding (DESIGN.md feature-card-tabbed)
 *  - dark backdrop
 *
 * Sizes: sm (max-w-md), md (max-w-2xl, default), lg (max-w-4xl), xl (max-w-6xl), full (max-w-[90vw])
 */
const SIZE_CLASSES = {
  sm: "max-w-md",
  md: "max-w-2xl",
  lg: "max-w-4xl",
  xl: "max-w-6xl",
  full: "max-w-[90vw]",
};

export function Modal({
  isOpen,
  onClose,
  title,
  description,
  size = "md",
  children,
  footer,
  className,
  panelClassName,
  initialFocus,
  hideHeader = false,
  bodyPadding = true,
}) {
  return (
    <Transition appear show={!!isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={onClose}
        initialFocus={initialFocus}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-ink/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-150"
              enterFrom="opacity-0 translate-y-2"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-100"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-2"
            >
              <Dialog.Panel
                className={cn(
                  "w-full bg-canvas border border-hairline rounded-lg shadow-card overflow-hidden flex flex-col",
                  SIZE_CLASSES[size] || SIZE_CLASSES.md,
                  panelClassName,
                  className
                )}
              >
                {!hideHeader && (title || description) ? (
                  <div className="px-8 pt-8 pb-4 border-b border-hairline">
                    {title ? (
                      <Dialog.Title className="text-display-md text-ink">
                        {title}
                      </Dialog.Title>
                    ) : null}
                    {description ? (
                      <Dialog.Description className="mt-2 text-body-md text-body">
                        {description}
                      </Dialog.Description>
                    ) : null}
                  </div>
                ) : null}

                <div className={cn("flex-1 overflow-y-auto", bodyPadding && "p-8")}>
                  {children}
                </div>

                {footer ? (
                  <div className="px-8 py-5 border-t border-hairline flex items-center justify-end gap-3 bg-surface-soft">
                    {footer}
                  </div>
                ) : null}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default Modal;
